/**
 * Cross-tab refresh lock — Web Locks API with localStorage fallback.
 */

const REFRESH_LOCK_NAME = 'codemerlin-auth-refresh';
const LS_LOCK_KEY = 'codemerlin-auth-refresh-lock';
const LS_LOCK_TTL_MS = 30_000;
const LS_LOCK_WAIT_MS = 15_000;
const LS_LOCK_POLL_MS = 50;

const tabId =
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `tab-${Date.now()}-${Math.random()}`;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

interface LsLockRecord {
  tabId: string;
  until: number;
}

function readLsLock(): LsLockRecord | null {
  try {
    const raw = localStorage.getItem(LS_LOCK_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as LsLockRecord;
    if (typeof parsed.tabId !== 'string' || typeof parsed.until !== 'number') {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function tryAcquireLsLock(): boolean {
  const now = Date.now();
  const existing = readLsLock();
  if (existing && existing.until > now && existing.tabId !== tabId) {
    return false;
  }
  localStorage.setItem(
    LS_LOCK_KEY,
    JSON.stringify({ tabId, until: now + LS_LOCK_TTL_MS } satisfies LsLockRecord)
  );
  const after = readLsLock();
  return after?.tabId === tabId;
}

function releaseLsLock(): void {
  const existing = readLsLock();
  if (existing?.tabId === tabId) {
    localStorage.removeItem(LS_LOCK_KEY);
  }
}

async function withLocalStorageRefreshLock<T>(fn: () => Promise<T>): Promise<T> {
  const deadline = Date.now() + LS_LOCK_WAIT_MS;
  while (Date.now() < deadline) {
    if (tryAcquireLsLock()) {
      try {
        return await fn();
      } finally {
        releaseLsLock();
      }
    }
    await sleep(LS_LOCK_POLL_MS);
  }
  throw new Error('Session refresh lock timeout');
}

/** Serialize refresh across tabs (rotation-safe). */
export async function withRefreshLock<T>(fn: () => Promise<T>): Promise<T> {
  if (typeof navigator !== 'undefined' && navigator.locks?.request) {
    return navigator.locks.request(REFRESH_LOCK_NAME, fn);
  }
  if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
    return withLocalStorageRefreshLock(fn);
  }
  return fn();
}
